import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import { AppDeployment, AppListingTab } from '@atlassian/mpac-types';
import { MarketplaceAppPaymentModel } from '@atlassian/mpac-types/graphql-gateway-types';
import { appSupportsEditions, convertToAtlassianHostingType } from '@atlassian/mpac-utils';
import React from 'react';
import { LazySuspense } from 'react-loosely-lazy';
import { TEST_ID } from '../../constants';
import { getTrustTabPrecondition, isSelectedHostingAllowedForPrivacyTab, pickVersionInfo } from './AppListing.utils';
import { LoadingSuspenseSpinner } from './Loading';
import { AppListingTabContentSkeleton } from './Skeleton';
import { LazyCustomerPrivacyAndSecurityTab, LazyPrivacySecurityDetailsNotAvailableBannerV2 } from './tabs/CustomerPrivacyAndSecurityTab';
import LasyInstallationTab from './tabs/InstallationTab/InstallationTabV2';
import { LazyOverviewTabV2 } from './tabs/OverviewTab';
import LazyPricingTabV2 from './tabs/PricingTab/PricingTabV2';
import { LazyReviewsTabV2 } from './tabs/ReviewsTab';
import { SupportTab as SupportTabV2 } from './tabs/SupportTab/v2/SupportTab';
import { jsx as _jsx } from "react/jsx-runtime";
/* eslint-disable complexity */
// eslint-disable-next-line max-lines-per-function -- function is thin on logic
export const makeAppListingTabs = _ref => {
  let {
    /* eslint sort-keys: "error" */
    appListing,
    availableSiteCloudIds,
    editions,
    selectedHosting,
    showInstallationTab,
    serverEolStartDate,
    showIntegrationsDetailsSection,
    showReviewsTab,
    showPricingTab,
    mpacBaseUrl,
    mpacCdnBaseUrl,
    atlassianCommunityBaseUrl,
    user,
    loginHref,
    onSupportLinkClick,
    onMediaViewerOpen,
    onSeeMoreReviewsClick,
    onArtifactDownloadClick,
    cloudMigration,
    onCloudMigrationResourceLinksClick,
    onPricingCalculatorInstallCTAClick,
    appRating,
    showServerEolMessage,
    feedbackCollectorApiKeys,
    feedbackGatewayBaseUrl,
    isServerAppEos,
    onTabSelect,
    appPricingDetailsLink,
    isEditionsSelfServeEnabled,
    billingSystem,
    trustCenterUrl,
    hostPricing,
    eligibleOfferings,
    eligibleForFreeOnSandbox,
    installedEdition,
    isSandbox
  } = _ref;
  const tabs = [];
  const trustTabPrecondition = getTrustTabPrecondition(selectedHosting, appListing);
  const isEditionsSupported = isEditionsSelfServeEnabled && appSupportsEditions({
    editions,
    billingSystem,
    hostingType: selectedHosting
  });

  // Overview Tab
  tabs.push({
    label: 'Overview',
    content: /*#__PURE__*/_jsx(LazySuspense, {
      fallback: /*#__PURE__*/_jsx(UFOLoadHold, {
        name: "app-listing-overview-tab",
        children: /*#__PURE__*/_jsx(AppListingTabContentSkeleton, {})
      }),
      children: /*#__PURE__*/_jsx(LazyOverviewTabV2, {
        appListing: appListing,
        isEditionsSupported: isEditionsSupported,
        editions: editions,
        appPricingDetailsLink: appPricingDetailsLink,
        hosting: convertToAtlassianHostingType(selectedHosting),
        isServerAppEos: isServerAppEos,
        mpacBaseUrl: mpacBaseUrl,
        onArtifactDownloadClick: onArtifactDownloadClick,
        onMediaViewerOpen: onMediaViewerOpen,
        onTabSelect: onTabSelect,
        showIntegrationsDetailsSection: showIntegrationsDetailsSection,
        versionInfo: pickVersionInfo(appListing),
        trustCenterUrl: trustCenterUrl
      })
    }),
    testId: TEST_ID.APP_LISTING_TAB(AppListingTab.Overview),
    key: AppListingTab.Overview
  });

  // Reviews Tab
  if (showReviewsTab) {
    tabs.push({
      label: 'Reviews',
      key: AppListingTab.Reviews,
      testId: TEST_ID.APP_LISTING_TAB(AppListingTab.Reviews),
      content: /*#__PURE__*/_jsx(LazySuspense, {
        fallback: /*#__PURE__*/_jsx(LoadingSuspenseSpinner, {
          name: "app-listing-reviews-tab"
        }),
        children: /*#__PURE__*/_jsx(LazyReviewsTabV2, {
          appKey: appListing.appKey,
          mpacBaseUrl: mpacBaseUrl,
          hosting: selectedHosting,
          vendorName: appListing.partner.name,
          vendorLogo: appListing.partner.logo,
          onSeeMoreReviewsClick: onSeeMoreReviewsClick,
          user: user,
          loginHref: loginHref,
          appRating: appRating,
          cloudCompatibleProducts: appListing.cloudCompatibleProducts,
          appDeploymentType: appListing.deploymentType,
          productHostingOptions: appListing.productHostingOptions,
          isServerAppEos: isServerAppEos
        })
      })
    });
  }

  // Pricing Tab
  if (showPricingTab && appListing.paymentModel && appListing.paymentModel !== MarketplaceAppPaymentModel.Free && !isServerAppEos) {
    tabs.push({
      label: 'Pricing',
      content: /*#__PURE__*/_jsx(LazySuspense, {
        fallback: /*#__PURE__*/_jsx(LoadingSuspenseSpinner, {
          name: "app-listing-pricing-tab"
        }),
        children: /*#__PURE__*/_jsx(LazyPricingTabV2, {
          appId: appListing.appId,
          appKey: appListing.appKey,
          compatibleProducts: appListing.compatibleProducts,
          hasDataCenterVersion: appListing.hasDataCenterVersion,
          hosting: selectedHosting,
          learnMoreUrl: appListing.learnMoreUrl,
          marketingLabels: appListing.marketingLabels,
          partnerType: appListing.partner.partnerType,
          paymentModel: appListing.paymentModel,
          mpacBaseUrl: mpacBaseUrl,
          onPricingCalculatorInstallCTAClick: onPricingCalculatorInstallCTAClick,
          showServerEolMessage: showServerEolMessage,
          serverEolStartDate: serverEolStartDate,
          isEditionsSupported: isEditionsSupported,
          availableSiteCloudIds: availableSiteCloudIds,
          hostPricing: hostPricing,
          eligibleOfferings: eligibleOfferings,
          eligibleForFreeOnSandbox: eligibleForFreeOnSandbox,
          installedEdition: installedEdition,
          isSandbox: isSandbox,
          editions: editions
        })
      }),
      testId: TEST_ID.APP_LISTING_TAB(AppListingTab.Pricing),
      key: AppListingTab.Pricing
    });
  }

  // Privacy and Security Tab
  if (!isServerAppEos) {
    var _appListing$partner;
    const {
      emailId
    } = appListing.supportDetails.contact;
    tabs.push({
      label: 'Privacy & Security',
      content: /*#__PURE__*/_jsx(LazySuspense, {
        fallback: /*#__PURE__*/_jsx(LoadingSuspenseSpinner, {
          name: "app-listing-privacy-and-security-tab"
        }),
        children: trustTabPrecondition ? /*#__PURE__*/_jsx(LazyCustomerPrivacyAndSecurityTab, {
          mpacBaseUrl: mpacBaseUrl,
          mpacCdnBaseUrl: mpacCdnBaseUrl,
          appKey: appListing.appKey,
          appId: appListing.appId,
          appName: appListing.name,
          feedbackCollectorApiKeys: feedbackCollectorApiKeys,
          feedbackGatewayBaseUrl: feedbackGatewayBaseUrl,
          userId: user.userId,
          supportEmailId: emailId,
          trustCenterUrl: trustCenterUrl
        }) : /*#__PURE__*/_jsx(LazyPrivacySecurityDetailsNotAvailableBannerV2, {
          isHostingCloud: isSelectedHostingAllowedForPrivacyTab(selectedHosting),
          isWorkflowApp: appListing.deploymentType === AppDeployment.WORKFLOW,
          appName: appListing.name,
          appKey: appListing.appKey,
          supportEmailId: emailId,
          mpacBaseUrl: mpacBaseUrl,
          partnerId: (_appListing$partner = appListing.partner) === null || _appListing$partner === void 0 ? void 0 : _appListing$partner.id
        })
      }),
      testId: TEST_ID.APP_LISTING_TAB(AppListingTab.PrivacyAndSecurity),
      key: AppListingTab.PrivacyAndSecurity
    });
  }

  // Support Tab
  if (!isServerAppEos) {
    tabs.push({
      label: 'Support',
      content: /*#__PURE__*/_jsx(SupportTabV2, {
        isEditionsSupported: isEditionsSupported,
        onTabSelect: onTabSelect,
        appKey: appListing.appKey,
        atlassianCommunityBaseUrl: atlassianCommunityBaseUrl,
        cloudMigrationInfo: cloudMigration,
        hosting: convertToAtlassianHostingType(selectedHosting),
        mpacBaseUrl: mpacBaseUrl,
        onCloudMigrationResourceLinksClick: onCloudMigrationResourceLinksClick,
        onSupportLinkClick: onSupportLinkClick,
        supportDetails: appListing.supportDetails,
        appPricingDetailsLink: appPricingDetailsLink
      }),
      testId: TEST_ID.APP_LISTING_TAB(AppListingTab.Support),
      key: AppListingTab.Support
    });
  }

  // Installation Tab
  if (showInstallationTab) {
    tabs.push({
      label: 'Installation',
      content: /*#__PURE__*/_jsx(LazySuspense, {
        fallback: /*#__PURE__*/_jsx(LoadingSuspenseSpinner, {
          name: "app-listing-installation-tab"
        }),
        children: /*#__PURE__*/_jsx(LasyInstallationTab, {
          isEditionsSupported: isEditionsSupported,
          appName: appListing.name,
          compatibleProducts: appListing.compatibleProducts,
          deploymentType: appListing.deploymentType,
          documentationUrl: appListing.documentationUrl,
          hosting: selectedHosting,
          installationInstructions: appListing.installationInstructions,
          paymentModel: appListing.paymentModel,
          versionCount: appListing.versionCount,
          versionHistoryUrl: appListing.versionHistoryUrl
        })
      }),
      testId: TEST_ID.APP_LISTING_TAB(AppListingTab.Installation),
      key: AppListingTab.Installation
    });
  }
  return tabs;
};